import MultiDateSelect, { TMultiDateSelect } from '../widgets/MultiDateSelect';
import CategoryGroupSelect, {
  ICategoryGroupOption,
  ICategoryGroupValue,
  TCategoryGroupSelectType,
} from '../widgets/CategoryGroupSelect';
import {
  IDateCommonSelectType,
  TComponentConfig,
} from '../types';
import {
  getDateDefaultValue,
} from '../utils';
import GameSelect, { TGameSelectType } from '../components/GameSelect';
import { GAME_CATEGORY } from '../constants/games';

type TCreativeTrendsGameSelectType = TGameSelectType & {
  defaultCategory?: string;
};

/**
 * Category cond 数据转换：
 * cond 接口：[{ value, label_en, label_zh, children: [{ value, label_en, label_zh }] }]
 * widget 期望：[{ value, text, textCn, children: [{ value, text, textCn }] }]
 *
 * 跨业务复用（CreativeTrends / UgcCreativeTrends 等所有 CategoryGroupSelect 场景）。
 */
export function dealwithCategoryGroupOptions(condInfo: any, key: string) {
  const data = condInfo[key] || [];
  const result: ICategoryGroupOption[] = data.map((item: any) => ({
    value: item.value,
    text: item.label_en,
    textCn: item.label_zh,
    children: (item.children || []).map((child: any) => ({
      value: child.value,
      text: child.label_en,
      textCn: child.label_zh,
    })),
  }));
  return result;
}

/**
 * Category 默认值：全选所有 L1 + 各自全部 L2 children
 * 若 userDefaultValue / 缓存中已有 categories，直接沿用
 *
 * 跨业务复用（与 categoryBackValueFunc 是一对，全选 ↔ 空数组 [] 的对称契约）。
 */
export function categoryDefaultValueFunc(
  filterValue: any,
  props: TCategoryGroupSelectType,
) {
  const { options, valueKeys: key } = props;
  const list = options || [];
  const existing = filterValue?.[key];

  if (Array.isArray(existing) && existing.length > 0) {
    return { [key]: existing as ICategoryGroupValue[] };
  }

  const allSelected: ICategoryGroupValue[] = list.map((opt) => ({
    l1: opt.value,
    l2: (opt.children || []).map((c) => c.value),
  }));
  return { [key]: allSelected };
}

/**
 * 「完全没选」哨兵 entry —— 保持数组元素形态统一为 `{ l1, l2 }`。
 *
 * 后端契约：list 接口的 categories 参数
 *   - `[]`                          → 全选（不过滤）
 *   - `[{ l1: '-99999', l2: [] }]`  → 完全没选（强制空结果，与"全选"语义对称）
 *   - `[{ l1, l2: [...] }, ...]`    → 具体选择
 */
const EMPTY_CATEGORY_SENTINEL: ICategoryGroupValue = {
  l1: '-99999',
  l2: [],
};

/**
 * Category backValue：三态映射到后端契约
 *   - 完全没选 → `[{ l1: '-99999', l2: [] }]`（哨兵 entry）
 *   - 全选     → `[]`（后端约定空数组等价于全选）
 *   - 部分选择 → 原样回传 `Array<{ l1, l2 }>`
 *
 * 跨业务复用。
 */
export function categoryBackValueFunc(
  filterValue: any,
  props: TCategoryGroupSelectType,
) {
  const { valueKeys: key, options } = props;
  const list = options || [];
  const values: ICategoryGroupValue[] = filterValue?.[key] || [];

  // 完全没选：value 数组本身为空，或所有项的 l2 都为空
  const totalSelected = values.reduce(
    (sum, it) => sum + (it?.l2?.length || 0),
    0,
  );
  if (totalSelected === 0) {
    return { [key]: [EMPTY_CATEGORY_SENTINEL] };
  }

  const isAll =
    list.length > 0 &&
    values.length === list.length &&
    values.every((it) => {
      const target = list.find((opt) => opt.value === it.l1);
      const total = target?.children?.length || 0;
      return total > 0 && (it.l2 || []).length === total;
    });

  if (isAll) {
    return { [key]: [] };
  }
  return { [key]: values };
}

/**
 * CTMultiDateRange — 日期范围筛选器（支持日/周/月三种粒度）
 *
 * - typeList：传给 RangePicker，长度 > 1 时会显示粒度切换器
 *   值需用 RangePicker 的内部 type（date / week / month），由 dayToDaily 映射成服务端字段（daily/weekly/monthly）
 * - optionsMap：粒度切换器各项的展示文案
 */
export const CTMultiDateRange: TComponentConfig<TMultiDateSelect> = {
  Component: MultiDateSelect,
  props: {
    valueKeys: {
      start_time: 'start_time',
      end_time: 'end_time',
      date_type: 'date_type',
    },
    typeList: ['date'],
    optionsMap: {
      date: { value: 'date', text: 'Day', text_cn: '日' },
      week: { value: 'week', text: 'Week', text_cn: '周' },
      month: { value: 'month', text: 'Month', text_cn: '月' },
    },
    className: 'dbom-ct-multi-date-range',
  },
  defaultValue: (filterValue: any, props: IDateCommonSelectType) => {
    return getDateDefaultValue(filterValue, props);
  },
};

/**
 * CTCategory — Creative Trends 类别筛选器
 *
 * 二级分组多选：
 * - cond 接口返回 [{ value, label_en, label_zh, children: [...] }]
 * - 内部存储 Array<{ l1: string; l2: string[] }>
 * - 提交给 list 接口 categories: Array<{ l1, l2 }>，全选时回退为 []
 */
export const CTCategory: TComponentConfig<TCategoryGroupSelectType> = {
  Component: CategoryGroupSelect,
  defaultValue: categoryDefaultValueFunc,
  backValue: categoryBackValueFunc,
  condDealwith: dealwithCategoryGroupOptions,
  requestKey: 'creative_trends_config_topic_types',
  props: {
    title: 'Category',
    titleCn: '分类',
    valueKeys: 'categories',
    options: [],
    maxShow: 2,
  },
};


/**
 * 游戏过滤器配置
 *
 * 功能说明：
 * 1. 游戏分类使用 Segment 样式平铺显示
 * 2. 游戏选择使用多选下拉组件
 * 3. 切换分类时自动过滤已选游戏，只保留属于当前分类的游戏
 * 4. 默认选中所有游戏
 */
export const CTGame: TComponentConfig<TCreativeTrendsGameSelectType> = {
  // 渲染组件
  Component: GameSelect,

  // 计算默认值
  defaultValue: (filterValue: any, props: TCreativeTrendsGameSelectType) => {
    const { valueKeys, options } = props;
    const { category, games } = valueKeys;
    const defaultCategory = props.defaultCategory || GAME_CATEGORY.ALL;
    const defaultGameOptions =
      defaultCategory === GAME_CATEGORY.ALL
        ? options
        : options.filter((item) => item.genre === defaultCategory);
    return {
      [category]: defaultCategory,
      [games]:
        filterValue?.[games] || defaultGameOptions.map((item) => item.game_id),
    };
  },

  // 返回值转换
  backValue: (filterValue: any, props: TGameSelectType) => {
    const { valueKeys, options } = props || {};
    const { category, games } = valueKeys;
    const value = filterValue?.[games] || [];

    // 全选：返回空数组
    if (value.length === options.length && value.length > 0) {
      return {
        [category]: filterValue[category],
        [games]: [],
      };
    }

    // 全不选：返回 ["-99999"]
    if (value.length === 0) {
      return {
        [category]: filterValue[category],
        [games]: ['-99999'],
      };
    }

    // 部分选择：返回选中的 games
    return {
      [category]: filterValue[category],
      [games]: value,
    };
  },
  requestKey: 'creative_trends_config_games',
  // 组件属性
  props: {
    title: 'Games',
    titleCn: '游戏',
    valueKeys: {
      category: 'game_category',
      games: 'game_ids',
    },
    allOption: { text: 'All Games', textCn: '全部游戏', value: 'all' },
    // 注意：实际游戏选项由组件通过 getGamesByCategory 动态获取
    options: [],
    listWidth: 350,
    listHeight: 300,
    searchable: true,
    matchButtonWidth: false,
  },
};
